import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { ModalAreYouSureWithBody } from 'components/Modals'
import { useState } from 'react'
import { getSystemNameLabel, type SystemName } from '../api/api'

export interface LedgerConnectionProps {
  systemName: SystemName | undefined
  isLoading: boolean
  isError: boolean
  isDisconnecting: boolean
  onDisconnect: () => Promise<unknown>
}

export const LedgerConnection = ({ systemName, isLoading, isError, isDisconnecting, onDisconnect }: LedgerConnectionProps) => {
  const [isConfirming, setIsConfirming] = useState(false)

  const handleConfirm = () =>
    onDisconnect()
      .then(() => setIsConfirming(false))
      .catch(() => undefined)

  return (
    <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
      {isError ? (
        <Typography variant="body2" color="error">
          Could not load the accounting system connection
        </Typography>
      ) : systemName ? (
        <Tooltip placement="top" title={systemName}>
          <Typography variant="body1">{getSystemNameLabel(systemName)}</Typography>
        </Tooltip>
      ) : (
        <Typography variant="body1" color="textSecondary">
          {isLoading ? 'Loading…' : 'Not connected'}
        </Typography>
      )}
      {systemName && !isError && (
        <Button
          variant="outlined"
          color="error"
          size="medium"
          disabled={isLoading || isDisconnecting}
          onClick={() => setIsConfirming(true)}
        >
          Disconnect
        </Button>
      )}
      {isConfirming && (
        <ModalAreYouSureWithBody
          title="Disconnect accounting system"
          bodyText="The customer will have to reconnect from the app before exports can resume."
          confirmText="Yes, disconnect"
          confirmColor="error"
          loading={isDisconnecting}
          onClose={() => setIsConfirming(false)}
          onContinue={handleConfirm}
        />
      )}
    </Stack>
  )
}
